import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Icons from '@/components/icons/IconMap'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import './Sidebar.css'

interface NavItem {
  label: string
  path: string
  iconKey: keyof typeof Icons
  counter?: number
}

interface NavSection {
  label: string
  items: NavItem[]
}

// AA-7/AA-14/AA-15 (MASTER.md V): trabajo diario primero, Configuración/Usuarios
// como sección secundaria de menor peso visual, Iniciativas fuera del menú
// principal hasta tener datos/flujo reales (la ruta /iniciativas sigue activa).
const PRIMARY_NAV_SECTION: NavSection = {
  label: 'Trabajo diario',
  items: [
    { label: 'Hoy', path: '/hoy', iconKey: 'Home' },
    { label: 'Empresas', path: '/relaciones', iconKey: 'Empresas' },
    { label: 'Personas', path: '/personas', iconKey: 'Contactos' },
    { label: 'Iniciativas', path: '/iniciativas', iconKey: 'Empresas' },
  ],
}

const SECONDARY_NAV_SECTION: NavSection = {
  label: 'Sistema',
  items: [{ label: 'Configuración', path: '/configuracion', iconKey: 'Configuracion' }],
}

const ADMIN_NAV_ITEM: NavItem = { label: 'Usuarios', path: '/usuarios', iconKey: 'Usuarios' }
const ONBOARDING_NAV_ITEM: NavItem = { label: 'Importar datos', path: '/onboarding', iconKey: 'Configuracion' }

export const Sidebar: React.FC = () => {
  const location = useLocation()
  const [searchTerm, setSearchTerm] = useState('')
  const currentUser = useCurrentUser()
  const isAdmin = currentUser.data?.user.rol === 'administrador'

  const isItemActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)

  const secondarySection: NavSection = isAdmin
    ? { ...SECONDARY_NAV_SECTION, items: [...SECONDARY_NAV_SECTION.items, ONBOARDING_NAV_ITEM, ADMIN_NAV_ITEM] }
    : SECONDARY_NAV_SECTION

  const navSections: NavSection[] = [PRIMARY_NAV_SECTION, secondarySection]

  const filteredSections = navSections.map(section => ({
    ...section,
    items: section.items.filter(item =>
      !searchTerm || item.label.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  })).filter(section => section.items.length > 0)

  return (
    <aside className="sidebar">
      {/* Sidebar Search */}
      <div className="sidebar__search">
        <input
          type="text"
          placeholder="Buscar vista..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className="sidebar__search-clear"
            onClick={() => setSearchTerm('')}
            title="Limpiar búsqueda"
          >
            <Icons.Close size="sm" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar__nav">
        {filteredSections.map((section) => (
          <div
            key={section.label}
            className={`sidebar__section ${section.label === 'Sistema' ? 'sidebar__section--secondary' : ''}`}
          >
            <div className="sidebar__section-label">
              <span>{section.label}</span>
            </div>
            <div className="sidebar__items">
              {section.items.map((item) => {
                const IconComponent = Icons[item.iconKey]
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar__item ${section.label === 'Sistema' ? 'sidebar__item--minor' : ''} ${isItemActive(item.path) ? 'sidebar__item--active' : ''}`}
                    title={item.label}
                  >
                    <span className="sidebar__icon">
                      <IconComponent size="md" />
                    </span>
                    <span className="sidebar__label">{item.label}</span>
                    {item.counter !== undefined && item.counter > 0 && (
                      <span className="sidebar__counter">{item.counter}</span>
                    )}
                  </Link>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="sidebar__footer">
        Wacner Sales
      </div>
    </aside>
  )
}
